import DataTool from 'echarts/dist/extension/dataTool';
import { EChartService } from './echart.service';
import { StatsCompetencyBoxplot } from '@/models/statistics/stats-competency-boxplot';
import { NumberUtils } from '@/utils/number.utils';
import { AvgAndSdData, AvgAndSds, AvgAndSd } from '@/models/statistics/stats-boxplot-subtable-data';
import { surveyAnalyzeModule } from '@/store/modules/frames/survey-analyze.module';
import { Activity } from '@/models/activity/activity';
import { SurveyActivityAnswer, SurveyAnswer } from '@/models/jgrow/survey-answer';
import { SurveyAnalysisCountAndPercent, SurveyAnalysisTabelMapping } from '@/models/jgrow/survey-analysis-tabel-mapping';

export class SurveyAnalysisMapEchartService extends EChartService {
  activities: Activity[] = [];
  breforeSeries: any[] = [];
  afterSeries: any[] = [];

  subTableColmunsArray: any[];
  subTableDataArray: any[];
  subTableAvgAndSd: AvgAndSdData;

  constructor(activities: Activity[] = []) {
    super();
    this.series = [];
    this.labels = [];
    this.subTableColmunsArray = [];
    this.subTableDataArray = [];
    this.subTableAvgAndSd = {};

    this.activities = Array.from(activities).sort((a, b: Activity) => b.activityID - a.activityID);
  }

  get subTableColmuns(): any[] {
    return this.subTableColmunsArray;
  }

  get subTableData(): any[] {
    return this.subTableDataArray;
  }

  get activitiyNames(): string[][] {
    const values: string[][] = new Array(this.activities.length);
    this.activities.map((a: Activity, i) => {
      values[i] = new Array(3);
      const wrappingCount = 14;
      if (a.activityName.length < wrappingCount + 1) {
        values[i][0] = a.activityName;
        values[i][1] = '';
        values[i][2] = '';
      } else if (a.activityName.length < wrappingCount * 2 + 1) {
        values[i][0] = a.activityName.slice(0, wrappingCount);
        values[i][1] = a.activityName.slice(wrappingCount, a.activityName.length);
        values[i][2] = '';
      } else {
        values[i][0] = a.activityName.slice(0, wrappingCount);
        values[i][1] = a.activityName.slice(wrappingCount, wrappingCount * 2);
        values[i][2] = a.activityName.slice(wrappingCount * 2, a.activityName.length);
      }
    });
    return values;
  }

  public addSeries(chartData: any): void {
    this.setBreforeSeries(this.makeBarGraphSeries(1));
    this.setAfterSeries(this.makeBarGraphSeries(2));
  }

  makeBarGraphSeries(type: number): any[] {
    const allActivityAnswers: SurveyActivityAnswer[] = new Array();
    const series: any[] = new Array();
    // 7 段階
    const stageCount = 7;
    const surveyCountMap: Map<number, SurveyAnalysisTabelMapping> = new Map();
    const nameMap = surveyAnalyzeModule.activityNameMap;

    surveyAnalyzeModule.answers.filter((sa: SurveyAnswer) => sa.type === type).map((sa: SurveyAnswer) => sa.activityAnswers.map((sac: SurveyActivityAnswer) => allActivityAnswers.push(sac)));

    nameMap.forEach((_: String, key: number) => {
      surveyCountMap.set(key, new SurveyAnalysisTabelMapping().deserialize({ id: key, stageCount: stageCount, size: surveyAnalyzeModule.totalUser }));
    });

    allActivityAnswers.map((an: SurveyActivityAnswer) => {
      const surveyCount = surveyCountMap.get(an.activityID);
      if (surveyCount) surveyCount.countUpdateByRatio(an.score);
    });

    // 回答なしを更新、％に変換
    surveyCountMap.forEach((value: SurveyAnalysisTabelMapping) => {
      value.unanswerdUpdate();
      value.trancePercent();
    });

    const dataMap: Map<number, number[]> = new Map();

    surveyCountMap.forEach((value: SurveyAnalysisTabelMapping) => {
      // レコードが存在しない場合は新規でマップに追加する。
      value.pointList.map((p: SurveyAnalysisCountAndPercent, i) => {
        if (!dataMap.get(i)) {
          dataMap.set(i, [p.items]);
        } else {
          // 存在する場合は配列に追加する。
          dataMap.get(i).push(p.items);
        }
      });
    });

    dataMap.forEach(value => value.reverse());

    for (let i = 0; i < 8; i++) {
      series.push({
        name: this.getLegendLabels(type - 1)[i],
        type: 'bar',
        stack: 'positive',
        itemStyle: {
          normal: {
            label: {
              show: true,
              position: 'inside',
              formatter: function(params: any) {
                if (params.value == 0) return '';
                return (i < 7 ? String(i + 1) : '回答なし') + ': ' + params.value + '人';
              },
            },
          },
        },
        labelLayout: { hideOverlap: true },
        data: dataMap.get(i),
      });
    }

    return series;
  }

  get total(): number {
    return surveyAnalyzeModule.totalUser;
  }

  setBreforeSeries(series: any[]) {
    this.breforeSeries = series;
  }

  setAfterSeries(series: any[]) {
    this.afterSeries = series;
  }

  isBefore(i: number): boolean {
    return i === 0;
  }

  getLegendLabels(i: number): string[] {
    if (this.isBefore(i)) {
      return [
        '1:全く期待していない',
        '2:期待していない',
        '3:どちらかと言えば\n   期待していない',
        '4:どちらとも言えない',
        '5:どちらかといえば\n   期待している',
        '6:期待している',
        '7:非常に期待している',
        '回答なし',
      ];
    } else {
      return ['1:非常に消極的', '2:消極的', '3:どちらかと言えば\n   消極的', '4:どちらとも言えない', '5:どちらかと言えば\n   積極的', '6:積極的', '7:非常に積極的', '回答なし'];
    }
  }

  get options() {
    const total = this.total;
    const options = new Array();
    for (let i: number = 0; i < 2; i++) {
      options.push({
        legend: {
          bottom: '0',
          show: true,
          data: this.getLegendLabels(i),
        },
        type: String(i + 1),
        grid: {
          bottom: '14%',
          top: '-3%',
          right: '-0.005%',
          left: '16%',
        },
        xAxis: {
          type: 'value',
          data: '',
          boundaryGap: true,
          min: 0,
          max: total,
          splitArea: {
            show: true,
          },
          axisLabel: {
            formatter: function(params: any) {
              return Math.floor((params / total) * 100) + '%';
            },
            rotate: 25,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'category',
          data: this.activitiyNames,
          axisLabel: {
            verticalAlign: 'center',
            padding: [0, 5, 0, 0],
            formatter: function(params: any) {
              const val = params.split(',');
              if (val[1] === '') return val[0];
              else if (val[2] === '') return val[0] + '\n' + val[1];
              else return val[0] + '\n' + val[1] + '\n' + val[2];
            },
          },
        },
        formatter: function(params: any) {
          return params;
        },
        tooltip: {
          formatter: function(param: any) {
            const line1: string = ` アクティビティ名：${param.name.replaceAll(',', '')}`;
            const line2: string = `${param.seriesName} `;
            const line3: string = `人数：${param.data} 人`;
            const line4: string = `割合：${Math.floor((param.data / total) * 100 * 10) / 10} %`;
            return [line1, line2, line3, line4].join('<br/>');
          },
        },
        calculable: true,
        series: this.isBefore(i) ? this.breforeSeries : this.afterSeries,
      });
    }

    return options;
  }
}
